<template>
    <v-container fluid class="fill-height candidate-login">
        <v-row align="center" justify="center" class="fill-height">
            <v-col cols="12" sm="8" md="6" lg="5">
                <v-card elevation="8" rounded="lg" class="candidate-card">
                    <v-card-title class="text-center pa-6">
                        <div class="d-flex flex-column align-center">
                            <v-img src="/logo.png" max-width="80" class="mb-4" alt="Logo Recruitment App"></v-img>
                            <h1 class="text-h4 mb-2">Espace Candidat</h1>
                            <p class="text-subtitle-1 text-grey-darken-1">
                                Gérez vos candidatures et suivez leur évolution
                            </p>
                        </div>
                    </v-card-title>

                    <v-card-text class="pa-6">
                        <v-tabs v-model="activeTab" centered class="mb-6">
                            <v-tab value="status">Vérifier le statut</v-tab>
                            <v-tab value="apply">Postuler</v-tab>
                        </v-tabs>

                        <v-tabs-window v-model="activeTab">
                            <!-- Onglet Vérification de statut -->
                            <v-tabs-window-item value="status">
                                <v-form @submit.prevent="handleStatusCheck" ref="statusForm">
                                    <v-text-field v-model="statusForm.email" label="Email de candidature" type="email"
                                        prepend-inner-icon="mdi-email" variant="outlined"
                                        :rules="[rules.required, rules.email]" required class="mb-4"></v-text-field>

                                    <v-text-field v-model="statusForm.applicationId"
                                        label="Numéro de candidature (optionnel)" prepend-inner-icon="mdi-identifier"
                                        variant="outlined" hint="Laissez vide pour voir toutes vos candidatures"
                                        persistent-hint class="mb-4"></v-text-field>

                                    <v-btn type="submit" color="primary" size="large" block :loading="isCheckingStatus"
                                        class="mb-4">
                                        <v-icon start>mdi-magnify</v-icon>
                                        Vérifier le statut
                                    </v-btn>
                                </v-form>
                            </v-tabs-window-item>

                            <!-- Onglet Candidature -->
                            <v-tabs-window-item value="apply">
                                <div class="text-center">
                                    <v-icon size="64" color="primary" class="mb-4">mdi-account-plus</v-icon>
                                    <h3 class="text-h6 mb-4">Première candidature ?</h3>
                                    <p class="text-body-2 mb-6 text-grey-darken-1">
                                        Postulez directement en ligne et suivez l'évolution de votre candidature
                                    </p>
                                    <v-btn color="success" size="large" block @click="showApplicationForm = true"
                                        variant="elevated">
                                        <v-icon start>mdi-file-document-plus</v-icon>
                                        Postuler maintenant
                                    </v-btn>
                                </div>
                            </v-tabs-window-item>
                        </v-tabs-window>

                        <v-divider class="my-6"></v-divider>

                        <!-- Lien vers l'espace employé/RH -->
                        <div class="text-center">
                            <p class="text-caption text-grey mb-2">Vous êtes employé ou RH ?</p>
                            <v-btn variant="text" color="primary" size="small" @click="goToHRSpace">
                                Accéder à l'espace employé
                            </v-btn>
                        </div>
                    </v-card-text>
                </v-card>
            </v-col>
        </v-row>

        <!-- Dialog Candidature -->
        <v-dialog v-model="showApplicationForm" max-width="800" scrollable>
            <CandidateApplicationForm @close="showApplicationForm = false" @submitted="handleApplicationSubmitted" />
        </v-dialog>

        <!-- Résultats du statut -->
        <v-dialog v-model="showStatusResults" max-width="700" scrollable>
            <v-card>
                <v-card-title class="d-flex align-center">
                    <v-icon class="mr-2">mdi-file-search</v-icon>
                    <span class="text-h6">Statut de vos candidatures</span>
                </v-card-title>

                <v-card-text>
                    <div v-if="statusResults.length > 0">
                        <v-card v-for="application in statusResults" :key="application.id" class="mb-4"
                            variant="outlined" elevation="2">
                            <v-card-text>
                                <div class="d-flex justify-space-between align-center mb-3">
                                    <h3 class="text-h6">{{ application.jobTitle }}</h3>
                                    <v-chip :color="getStatusColor(application.status)" size="small" variant="elevated">
                                        {{ getStatusText(application.status) }}
                                    </v-chip>
                                </div>

                                <v-row no-gutters class="mb-2">
                                    <v-col cols="auto" class="mr-4">
                                        <v-icon size="small" class="mr-1">mdi-identifier</v-icon>
                                        <strong>Candidature #:</strong> {{ application.id }}
                                    </v-col>
                                </v-row>

                                <v-row no-gutters class="mb-2">
                                    <v-col>
                                        <v-icon size="small" class="mr-1">mdi-calendar</v-icon>
                                        <strong>Date:</strong> {{ formatDate(application.appliedAt) }}
                                    </v-col>
                                </v-row>

                                <div v-if="application.notes" class="mt-3">
                                    <v-divider class="mb-2"></v-divider>
                                    <p class="text-body-2">
                                        <v-icon size="small" class="mr-1">mdi-note-text</v-icon>
                                        <strong>Notes:</strong> {{ application.notes }}
                                    </p>
                                </div>
                            </v-card-text>
                        </v-card>
                    </div>

                    <div v-else class="text-center py-8">
                        <v-icon size="64" color="grey-lighten-1">mdi-file-search-outline</v-icon>
                        <p class="text-h6 mt-4 mb-2">Aucune candidature trouvée</p>
                        <p class="text-body-2 text-grey">
                            Vérifiez votre email ou <v-btn variant="text" color="primary"
                                @click="activeTab = 'apply'">postulez pour un poste</v-btn>
                        </p>
                    </div>
                </v-card-text>

                <v-card-actions>
                    <v-spacer></v-spacer>
                    <v-btn @click="showStatusResults = false">Fermer</v-btn>
                    <v-btn color="primary" variant="text" @click="showStatusResults = false; activeTab = 'apply'">
                        Nouvelle candidature
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
    </v-container>
</template>

<script setup lang="ts">
import CandidateApplicationForm from '@/components/candidate/CandidateApplicationForm.vue'
import { apiService } from '@/services/api'
import { useCandidateStore } from '@/stores/candidate'
import { useSnackbarStore } from '@/stores/snackbar'
import { format } from 'date-fns'
import { fr } from 'date-fns/locale'
import { ref } from 'vue'
import { useRouter } from 'vue-router'

interface Application {
    id: string | number
    jobTitle: string
    status: string
    appliedAt: string
    notes?: string
}

const snackbarStore = useSnackbarStore()
const router = useRouter()
const candidateStore = useCandidateStore()

const activeTab = ref('status')
const showApplicationForm = ref(false)
const showStatusResults = ref(false)
const isCheckingStatus = ref(false)

const statusForm = ref({
    email: '',
    applicationId: ''
})

const statusResults = ref<Application[]>([])

// Fonction pour rediriger vers l'espace RH
const goToHRSpace = () => {
    window.location.href = '/?mode=hr'
}

const rules = {
    required: (value: string) => !!value || 'Ce champ est requis',
    email: (value: string) => /.+@.+\..+/.test(value) || 'Email invalide'
}

const handleStatusCheck = async () => {
    if (!statusForm.value.email) {
        snackbarStore.warning('Veuillez saisir votre email')
        return
    }

    try {
        isCheckingStatus.value = true
        const applicationId = statusForm.value.applicationId ? String(statusForm.value.applicationId) : undefined
        const response = await apiService.candidate.checkStatus(
            statusForm.value.email,
            applicationId
        )
        const applications = Array.isArray(response.data) ? response.data : response.data.applications
        if (applications && applications.length > 0) {
            // Stocker dans le store Pinia
            candidateStore.setApplications(applications)
            candidateStore.setCurrentApplication(applications[0])
            localStorage.setItem('candidate_email', statusForm.value.email)
            localStorage.setItem('candidate_applicationIds', JSON.stringify(applications.map(a => a.id)))
            statusResults.value = applications
            showStatusResults.value = true
            snackbarStore.success('Connexion réussie, candidatures trouvées')
            setTimeout(() => {
                router.push({ name: 'candidate-status' })
            }, 500)
        } else {
            snackbarStore.warning('Aucune candidature trouvée avec cet email')
        }
    } catch (error: any) {
        console.error('Erreur lors de la vérification du statut:', error)
        snackbarStore.error('Erreur lors de la vérification du statut')
    } finally {
        isCheckingStatus.value = false
    }
}

const handleApplicationSubmitted = (application: Application) => {
    showApplicationForm.value = false
    snackbarStore.success('Candidature envoyée avec succès')

    // Ajouter la nouvelle candidature aux résultats
    statusResults.value = [application]
    showStatusResults.value = true
}

const getStatusColor = (status: string) => {
    const colors = {
        'applied': 'blue',
        'reviewing': 'orange',
        'interview_scheduled': 'purple',
        'interview_completed': 'indigo',
        'accepted': 'green',
        'rejected': 'red',
        'on_hold': 'grey'
    }
    return colors[status as keyof typeof colors] || 'grey'
}

const getStatusText = (status: string) => {
    const texts = {
        'applied': 'Candidature reçue',
        'reviewing': 'En cours d\'examen',
        'interview_scheduled': 'Entretien programmé',
        'interview_completed': 'Entretien terminé',
        'accepted': 'Acceptée',
        'rejected': 'Refusée',
        'on_hold': 'En attente'
    }
    return texts[status as keyof typeof texts] || status
}

const formatDate = (date: string) => {
    return format(new Date(date), 'dd MMMM yyyy', { locale: fr })
}
</script>

<style scoped>
.candidate-login {
    background: linear-gradient(135deg, #4CAF50 0%, #2E7D32 100%);
    min-height: 100vh;
}

.candidate-card {
    backdrop-filter: blur(10px);
    background: rgba(255, 255, 255, 0.95);
}

.v-tabs {
    margin-bottom: 1rem;
}

.v-chip {
    font-weight: 500;
}
</style>
